import { ZonedDateTime } from '@internationalized/date';
import { OperationType, Unit } from '@rematter/frontend-lib-api-client';

export type VariableRecord = {
  id: string;
  name: string;
  description: string | null;
  value: number | null;
  updatedByUser?: { id: string; name: string } | null;
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  };
};

export type CounterpartyGroupRecord = {
  id: string;
  name: string;
  operationType: OperationType;
  memberCount: number;
  updatedByUser?: { id: string; name: string } | null;
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  };
};

export type UnitPriceFormulaRecord = {
  formula: string;
  variableIds: string[];
};

export type ItemPricingTierRecord = {
  id: string;
  maxAmount: number | null;
  minPrice: UnitPriceFormulaRecord;
  targetPrice: UnitPriceFormulaRecord;
  maxPrice: UnitPriceFormulaRecord;
  scrapValue: number;
  position: number;
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  };
};

export type ItemPricingRecord = {
  id: string;
  item: {
    id: string;
    name: string;
    itemGroup?: { id: string; name: string } | null;
  };
  pricingUnit: Unit;
  position: number;
  tiersEffectiveAt: ZonedDateTime | null;
  tiers: ItemPricingTierRecord[];
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  };
};

export type DefaultPriceListRecord = {
  id: string;
  facilityId: string;
  operationType: OperationType;
  currency: string;
  notes: string | null;
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  };
};

export type CustomPriceListRecord = {
  id: string;
  facilityId: string;
  name: string;
  counterpartyGroupId: string;
  currency: string;
  operationType: OperationType;
  pricingMode: 'Standard' | 'CRV';
  startDate: ZonedDateTime | null;
  endDate: ZonedDateTime | null;
  isPublished: boolean;
  archivedAt: ZonedDateTime | null;
  notes: string | null;
  updatedByUser?: { id: string; name: string } | null;
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  };
};

export type CustomPriceListStatus = 'Draft' | 'Active' | 'Expired' | 'Archived';

export const OPERATION_TYPE_COLORS: Record<
  OperationType,
  'warning' | 'success' | 'primary'
> = {
  Purchase: 'warning',
  Sale: 'success',
  Brokerage: 'primary',
};

export type CustomPriceListStatusCountsRecord = {
  active: number;
  expired: number;
  archived: number;
  draft: number;
};

export type EvaluatedTierRecord = {
  tier: ItemPricingTierRecord;
  minPrice: number | null;
  targetPrice: number | null;
  maxPrice: number | null;
};

export type EvaluatedItemPricingRecord = {
  itemPricing: ItemPricingRecord;
  evaluatedTiers: EvaluatedTierRecord[];
};

export type ItemPriceRecord = {
  itemPricings: EvaluatedItemPricingRecord[];
  sourceType: string;
  sourceId: string;
};

export type TierRow = {
  id: string;
  itemPricingId: string;
  itemId: string;
  itemName: string;
  itemGroupName: string | null;
  pricingUnit: Unit;
  itemPricingPosition: number;
  tier: ItemPricingTierRecord;
  tierCount: number;
  tierIndex: number;
};

export type TierInput = {
  maxAmount?: number | null;
  minPrice: { formula: string; variableIds: string[] };
  targetPrice: { formula: string; variableIds: string[] };
  maxPrice: { formula: string; variableIds: string[] };
  scrapValue: number;
  position: number;
};

export type ValidationIssue = {
  severity: 'error' | 'warning';
  itemName: string;
  tierIndex: number;
  message: string;
};

export type PendingVariableChange = {
  variableId: string;
  variableName: string;
  oldValue: number | null;
  newValue: number | null;
};

export type ItemPriceConfigurationRecord = {
  id: string;
  facilityId: string | null;
  operationType: OperationType;
  currency: string;
  itemId: string;
  pricingUnits: Record<string, boolean> | null;
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  };
};
